/**
 * Mock implementation of Node.js url module for browser environment
 */

export interface ParsedUrl {
  protocol?: string;
  hostname?: string;
  port?: string;
  pathname: string;
  search: string;
  query: Record<string, string> | string;
  hash: string;
  href: string;
}

// Mock url module functions
export const parse = (urlString: string, parseQueryString: boolean = false): ParsedUrl => {
  console.log(`[MockURL] parse: ${urlString}`);
  
  try {
    const url = new URL(urlString);
    const result: ParsedUrl = {
      protocol: url.protocol,
      hostname: url.hostname,
      port: url.port,
      pathname: url.pathname,
      search: url.search,
      hash: url.hash,
      href: url.href,
      query: parseQueryString ? Object.fromEntries(url.searchParams) : url.search
    };
    
    return result;
  } catch (e) {
    // Return a basic object for invalid URLs
    return { 
      pathname: urlString, 
      search: '',
      query: {},
      hash: '',
      href: urlString
    };
  }
};

export const pathToFileURL = (path: string): URL => {
  console.log(`[MockURL] pathToFileURL: ${path}`);
  
  // Convert file path to file:// URL
  let urlString = path;
  if (!urlString.startsWith('file://')) {
    // Normalize path separators and ensure it starts with /
    urlString = urlString.replace(/\\/g, '/');
    if (!urlString.startsWith('/')) {
      urlString = '/' + urlString;
    }
    urlString = 'file://' + urlString;
  }
  return new URL(urlString);
};

export const fileURLToPath = (url: URL | string): string => {
  console.log(`[MockURL] fileURLToPath: ${url}`);
  
  // Convert file:// URL back to file path
  if (typeof url === 'string') {
    url = new URL(url);
  }
  if (url.protocol !== 'file:') {
    throw new TypeError('URL must be a file URL');
  }
  return decodeURIComponent(url.pathname);
};

export const format = (urlObj: ParsedUrl): string => {
  console.log(`[MockURL] format`);
  
  try {
    const url = new URL('http://placeholder');
    if (urlObj.protocol) url.protocol = urlObj.protocol;
    if (urlObj.hostname) url.hostname = urlObj.hostname;
    if (urlObj.port) url.port = urlObj.port;
    if (urlObj.pathname) url.pathname = urlObj.pathname;
    if (urlObj.search) url.search = urlObj.search;
    if (urlObj.hash) url.hash = urlObj.hash;
    
    return url.href;
  } catch (e) {
    return urlObj.href || '';
  }
};

export const resolve = (from: string, to: string): string => {
  console.log(`[MockURL] resolve: ${from} -> ${to}`);
  
  try {
    return new URL(to, from).href;
  } catch (e) {
    return to;
  }
};

const URLCtor: typeof URL = typeof URL !== 'undefined' ? URL : (class URLFallback {
  href: string;
  constructor(input: string) {
    this.href = input;
  }
}) as unknown as typeof URL;

// Export all url functions
export default {
  parse,
  pathToFileURL,
  fileURLToPath,
  format,
  resolve,
  URL: URLCtor,
};

export { URLCtor as URL };
